import { useState, useCallback } from 'react';
import { getDashboardKpis, getDashboardTopDepartamentos, getVendasHorario } from '../services/api';
import { PageHeader, Erro, StatCard } from '../components/UI';
import FiltroData from '../components/FiltroData';
import { useAuth } from '../context/AuthContext';
import './Dashboard.css';

const fmt  = v => (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtN = v => (v ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtI = v => (v ?? 0).toLocaleString('pt-BR');

export default function Dashboard() {
  const { nome, empresa } = useAuth();
  const [periodo,   setPeriodo]   = useState(null);
  const [kpis,       setKpis]       = useState(null);
  const [deps,       setDeps]       = useState(null);
  const [horario,    setHorario]    = useState(null);
  const [loadKpis,   setLoadKpis]   = useState(false);
  const [loadDeps,   setLoadDeps]   = useState(false);
  const [loadHorario,setLoadHorario]= useState(false);
  const [erroKpis,   setErroKpis]   = useState('');
  const [erroDeps,   setErroDeps]   = useState('');
  const [erroHorario,setErroHorario]= useState('');

  const carregar = useCallback((p) => {
    if (!p?.di || !p?.df) return;
    setKpis(null); setDeps(null); setHorario(null);
    setErroKpis(''); setErroDeps(''); setErroHorario('');

    setLoadKpis(true);
    getDashboardKpis(p.di, p.df)
      .then(k => setKpis(k))
      .catch(e => setErroKpis(e.message))
      .finally(() => setLoadKpis(false));

    setLoadDeps(true);
    getDashboardTopDepartamentos(p.di, p.df, 10)
      .then(d => setDeps(d))
      .catch(e => setErroDeps(e.message))
      .finally(() => setLoadDeps(false));

    setLoadHorario(true);
    getVendasHorario(p.di, p.df)
      .then(d => setHorario(d))
      .catch(e => setErroHorario(e.message))
      .finally(() => setLoadHorario(false));
  }, []);

  const handlePeriodo = useCallback((p) => {
    setPeriodo(p);
    carregar(p);
  }, [carregar]);

  const hora = new Date().getHours();
  const saudacao = hora < 12 ? 'Bom dia' : hora < 18 ? 'Boa tarde' : 'Boa noite';

  return (
    <div>
      <PageHeader
        titulo={`${saudacao}, ${nome?.split(' ')[0]}!`}
        subtitulo={`${empresa}${periodo?.label ? ' · ' + periodo.label : ''}`}
      >
        <FiltroData onChange={handlePeriodo} defaultModo="mes" />
      </PageHeader>

      {/* ─── KPI Cards ───────────────────────────────────────────────────── */}
      {erroKpis && <Erro mensagem={erroKpis} onRetry={() => carregar(periodo)} />}
      <div className="dash-stats">
        {loadKpis ? (
          Array.from({ length: 5 }).map((_, i) => <div key={i} className="dash-skeleton-card" />)
        ) : kpis ? (
          <>
            <StatCard label="Faturamento"  valor={fmt(kpis.faturamento)}     sub={`${fmtI(kpis.qtd_efetuadas)} vendas`} cor="laranja" icone={<IcoMoney />} />
            <StatCard label="Ticket Médio" valor={fmt(kpis.ticket_medio)}    sub="por venda efetuada"                    cor="blue"    icone={<IcoTicket />} />
            <StatCard label="Efetuadas"    valor={fmtI(kpis.qtd_efetuadas)}  sub="vendas efetuadas"                      cor="green"   icone={<IcoCheck />} />
            <StatCard label="Canceladas"   valor={fmtI(kpis.qtd_canceladas)} sub="vendas canceladas"                     cor="yellow"  icone={<IcoCancel />} />
            <StatCard label="Estornadas"   valor={fmtI(kpis.qtd_estornadas)} sub="vendas estornadas"                     cor="red"     icone={<IcoReturn />} />
          </>
        ) : null}
      </div>

      {/* ─── Vendas por Horário ─────────────────────────────────────────── */}
      {erroHorario && <Erro mensagem={erroHorario} onRetry={() => carregar(periodo)} />}
      {(loadHorario || horario !== null) && (
        <div className="dash-card">
          <div className="dash-card-header">
            <span className="dash-card-titulo">Vendas por Horário</span>
            <span className="dash-card-sub">faturamento por hora · {periodo?.label}</span>
          </div>
          {loadHorario ? (
            <div className="dash-skeleton-rows" style={{ padding: '16px' }}>
              <div className="dash-skeleton-row" style={{ height: 150 }} />
            </div>
          ) : (
            <div style={{ padding: '8px 16px 16px' }}>
              <GraficoHorario dados={horario || []} />
            </div>
          )}
        </div>
      )}

      {/* ─── Top Departamentos ───────────────────────────────────────────── */}
      {erroDeps && <Erro mensagem={erroDeps} onRetry={() => carregar(periodo)} />}
      {(loadDeps || deps !== null) && (
        <div className="dash-card">
          <div className="dash-card-header">
            <span className="dash-card-titulo">Top Departamentos</span>
            <span className="dash-card-sub">por faturamento · {periodo?.label}</span>
          </div>

          {loadDeps ? (
            <div className="dash-skeleton-rows">
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="dash-skeleton-row" />)}
            </div>
          ) : deps?.length === 0 ? (
            <div className="dash-vazio">Sem dados de venda para o período.</div>
          ) : (() => {
            const totalVenda = deps.reduce((s, d) => s + (d.TVENDA || 0), 0);
            const part = (v) => totalVenda > 0 ? (v / totalVenda) * 100 : 0;
            return (
            <>
              {/* Desktop */}
              <div className="dash-tbl-wrap">
                <table className="dash-tbl">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Departamento</th>
                      <th className="dash-right">Qtd</th>
                      <th className="dash-right">Custo</th>
                      <th className="dash-right">Venda</th>
                      <th className="dash-right">Lucro Bruto</th>
                      <th className="dash-right">Margem</th>
                      <th className="dash-right">Markup</th>
                      <th className="dash-right">Participação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deps.map((d, i) => {
                      const p = part(d.TVENDA || 0);
                      return (
                        <tr key={i}>
                          <td className="dash-rank">{i + 1}</td>
                          <td className="dash-nome">{d.AGRUPAMENTO || '—'}</td>
                          <td className="dash-right">{fmtN(d.QTD)}</td>
                          <td className="dash-right dash-dim">{fmt(d.TCUSTO)}</td>
                          <td className="dash-right dash-bold">{fmt(d.TVENDA)}</td>
                          <td className="dash-right">{fmt(d.LUCRO_BRUTO)}</td>
                          <td className="dash-right">
                            <span className={`dash-pct ${(d.MARGEM || 0) >= 0 ? 'dash-pct--pos' : 'dash-pct--neg'}`}>
                              {d.MARGEM != null ? `${fmtN(d.MARGEM)}%` : '—'}
                            </span>
                          </td>
                          <td className="dash-right">
                            <span className={`dash-pct ${(d.MARKUP || 0) >= 0 ? 'dash-pct--pos' : 'dash-pct--neg'}`}>
                              {d.MARKUP != null ? `${fmtN(d.MARKUP)}%` : '—'}
                            </span>
                          </td>
                          <td className="dash-right">
                            <div className="dash-part-wrap">
                              <span className="dash-part-pct">{fmtN(p)}%</span>
                              <div className="dash-part-bar"><div className="dash-part-fill" style={{ width: `${p}%` }} /></div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile */}
              <div className="dash-mobile-list">
                {deps.map((d, i) => {
                  const p = part(d.TVENDA || 0);
                  return (
                  <div key={i} className="dash-dep-card">
                    <div className="ddc-header">
                      <span className="ddc-rank">{i + 1}</span>
                      <span className="ddc-nome">{d.AGRUPAMENTO || '—'}</span>
                      <span className="ddc-venda">{fmt(d.TVENDA)}</span>
                    </div>
                    <div className="ddc-part">
                      <div className="dash-part-bar"><div className="dash-part-fill" style={{ width: `${p}%` }} /></div>
                      <span className="dash-part-pct">{fmtN(p)}%</span>
                    </div>
                    <div className="ddc-row">
                      <span>Qtd: <strong>{fmtN(d.QTD)}</strong></span>
                      <span>Custo: <strong>{fmt(d.TCUSTO)}</strong></span>
                      <span>Lucro: <strong>{fmt(d.LUCRO_BRUTO)}</strong></span>
                    </div>
                    <div className="ddc-row">
                      <span>Margem: <strong className={`dash-pct ${(d.MARGEM || 0) >= 0 ? 'dash-pct--pos' : 'dash-pct--neg'}`}>{d.MARGEM != null ? `${fmtN(d.MARGEM)}%` : '—'}</strong></span>
                      <span>Markup: <strong className={`dash-pct ${(d.MARKUP || 0) >= 0 ? 'dash-pct--pos' : 'dash-pct--neg'}`}>{d.MARKUP != null ? `${fmtN(d.MARKUP)}%` : '—'}</strong></span>
                    </div>
                  </div>
                  );
                })}
              </div>
            </>
            );
          })()}
        </div>
      )}
    </div>
  );
}

// ─── Gráfico de barras: vendas por hora ──────────────────────────────────────
const fmtY = v => {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `${(v / 1_000).toFixed(0)}k`;
  return v.toFixed(0);
};

function GraficoHorario({ dados }) {
  const [hovIdx, setHovIdx] = useState(null);

  const horas = Array.from({ length: 24 }, (_, h) => {
    const d = dados.find(x => x.HORA === h);
    return { hora: h, qtd: d?.QTD || 0, valor: d?.VALOR || 0 };
  });

  const comVenda   = horas.filter(h => h.valor > 0);
  const totalVenda = comVenda.reduce((s, h) => s + h.valor, 0);
  const pico       = comVenda.reduce((a, h) => h.valor > (a?.valor ?? 0) ? h : a, null);

  const maxValor = Math.max(...horas.map(h => h.valor), 1);
  const W = 720, H = 160;
  const PAD = { top: 22, right: 12, bottom: 28, left: 50 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;
  const gap    = chartW / 24;
  const barW   = gap * 0.62;
  const bX = i => PAD.left + i * gap + (gap - barW) / 2;
  const bH = v => (v / maxValor) * chartH;
  const bY = v => PAD.top + chartH - bH(v);

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(t => t * maxValor);
  const h      = hovIdx !== null ? horas[hovIdx] : null;
  const tipLeft = hovIdx !== null
    ? `${((bX(hovIdx) + barW / 2) / W * 100).toFixed(1)}%`
    : '0%';

  return (
    <div className="dash-grafico-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" className="dash-grafico-svg">
        {/* Grid + Y labels */}
        {yTicks.map((v, i) => {
          const y = bY(v);
          return (
            <g key={i}>
              <line x1={PAD.left} x2={W - PAD.right} y1={y} y2={y}
                stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
              <text x={PAD.left - 5} y={y + 4} textAnchor="end" fontSize="9" fill="rgba(255,255,255,0.3)">
                {fmtY(v)}
              </text>
            </g>
          );
        })}

        {/* Bars + valor no topo + hover areas + X labels */}
        {horas.map((hd, i) => {
          const by = bY(hd.valor);
          const bh = Math.max(bH(hd.valor), 0);
          const isPico = pico && hd.hora === pico.hora;
          const isHov  = hovIdx === i;
          return (
            <g key={i} onMouseEnter={() => setHovIdx(i)} onMouseLeave={() => setHovIdx(null)}>
              <rect x={bX(i)} y={by} width={barW} height={bh}
                fill={isPico ? '#fb923c' : '#f97316'} rx="2"
                opacity={hd.valor === 0 ? 0.1 : isHov ? 1 : isPico ? 0.95 : 0.72}
                style={{ transition: 'opacity 0.12s' }} />
              {/* valor acima da barra (só horas com venda) */}
              {hd.valor > 0 && (
                <text x={bX(i) + barW / 2} y={by - 3}
                  textAnchor="middle" fontSize="8"
                  fill={isHov || isPico ? '#f97316' : 'rgba(255,255,255,0.45)'}>
                  {fmtY(hd.valor)}
                </text>
              )}
              {/* transparent hit area */}
              <rect x={PAD.left + i * gap} y={PAD.top} width={gap} height={chartH}
                fill="transparent" style={{ cursor: hd.valor > 0 ? 'crosshair' : 'default' }} />
              {(i % 4 === 0 || i === 23) && (
                <text x={bX(i) + barW / 2} y={H - PAD.bottom + 14}
                  textAnchor="middle" fontSize="9" fill="rgba(255,255,255,0.3)">
                  {String(hd.hora).padStart(2, '0')}h
                </text>
              )}
            </g>
          );
        })}

        {/* Dashed guide line on hover */}
        {h && h.valor > 0 && (
          <line x1={bX(hovIdx) + barW / 2} x2={bX(hovIdx) + barW / 2}
            y1={PAD.top} y2={bY(h.valor) - 12}
            stroke="rgba(249,115,22,0.25)" strokeWidth="1" strokeDasharray="3 3" />
        )}
      </svg>

      {/* Tooltip ao hover */}
      {h && h.valor > 0 && (
        <div className="dash-grafico-tooltip" style={{ left: tipLeft }}>
          <span className="dgt-hora">{String(h.hora).padStart(2,'0')}:00 – {String(h.hora + 1).padStart(2,'0')}:00</span>
          <span className="dgt-valor">{fmt(h.valor)}</span>
          <span className="dgt-qtd">{h.qtd} venda{h.qtd !== 1 ? 's' : ''}</span>
        </div>
      )}

      {/* Tabela sempre visível */}
      {comVenda.length > 0 && (
        <div className="dash-horario-tabela">
          <div className="dht-header">
            <span>Horário</span>
            <span>Vendas</span>
            <span className="dht-right">Faturamento</span>
            <span className="dht-right">Participação</span>
          </div>
          {comVenda.map(hd => (
            <div key={hd.hora}
              className={`dht-row${pico && hd.hora === pico.hora ? ' dht-row--pico' : ''}`}
              onMouseEnter={() => setHovIdx(hd.hora)}
              onMouseLeave={() => setHovIdx(null)}>
              <span className="dht-hora">{String(hd.hora).padStart(2,'0')}:00 – {String(hd.hora + 1).padStart(2,'0')}:00</span>
              <span>{hd.qtd} venda{hd.qtd !== 1 ? 's' : ''}</span>
              <span className="dht-right dht-valor">{fmt(hd.valor)}</span>
              <span className="dht-right">
                <div className="dash-part-wrap" style={{ minWidth: 80 }}>
                  <span className="dash-part-pct">{fmtN(totalVenda > 0 ? (hd.valor / totalVenda) * 100 : 0)}%</span>
                  <div className="dash-part-bar"><div className="dash-part-fill" style={{ width: `${totalVenda > 0 ? (hd.valor / totalVenda) * 100 : 0}%` }} /></div>
                </div>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function IcoMoney()  { return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>; }
function IcoTicket() { return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 9a3 3 0 010-6h20a3 3 0 010 6"/><path d="M2 15a3 3 0 000 6h20a3 3 0 000-6"/><line x1="2" y1="12" x2="22" y2="12"/></svg>; }
function IcoCheck()  { return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>; }
function IcoCancel() { return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>; }
function IcoReturn() { return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/></svg>; }
